import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../../../model/contexts/user.context";

import "./GoogleSignInButton.scss";

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const wait = (milliseconds) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

const loadGoogleIdentityServices = async () => {
  if (window.google?.accounts?.id) {
    return window.google;
  }

  let googleScript = document.querySelector(
    `script[src="${GOOGLE_SCRIPT_SRC}"]`,
  );

  if (!googleScript) {
    googleScript = document.createElement("script");
    googleScript.src = GOOGLE_SCRIPT_SRC;
    googleScript.async = true;
    googleScript.defer = true;
    googleScript.id = "google-identity-services-script";

    document.head.appendChild(googleScript);
  }

  for (let attempt = 1; attempt <= 100; attempt += 1) {
    if (window.google?.accounts?.id) {
      return window.google;
    }

    await wait(100);
  }

  throw new Error(
    "Google authentication could not be loaded. Please refresh and try again.",
  );
};

export default function GoogleSignInButton({ mode, resetFormInput }) {
  const googleButtonRef = useRef(null);
  const currentAuthValuesRef = useRef(null);

  const [renderStatus, setRenderStatus] = useState("waiting");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { googleSignIn, googleSignUp } = useUser();

  currentAuthValuesRef.current = {
    mode,
    resetFormInput,
    googleSignIn,
    googleSignUp,
  };

  const handleGoogleResponse = useCallback(
    async (response) => {
      const {
        mode: currentMode,
        resetFormInput: currentResetFormInput,
        googleSignIn: currentGoogleSignIn,
        googleSignUp: currentGoogleSignUp,
      } = currentAuthValuesRef.current;

      try {
        if (!response?.credential) {
          throw new Error("Google did not return a credential.");
        }

        setRenderStatus("authenticating");
        setErrorMessage("");

        if (currentMode === "signup") {
          if (typeof currentGoogleSignUp !== "function") {
            throw new Error("Google sign-up is currently unavailable.");
          }

          await currentGoogleSignUp(response.credential);
        } else if (currentMode === "signin") {
          if (typeof currentGoogleSignIn !== "function") {
            throw new Error("Google sign-in is currently unavailable.");
          }

          await currentGoogleSignIn(response.credential);
        } else {
          throw new Error("Invalid Google authentication mode.");
        }

        if (typeof currentResetFormInput === "function") {
          currentResetFormInput();
        }

        navigate("/project-dashboard");
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while signing in with Google.";

        setRenderStatus("error");
        setErrorMessage(message);
        alert(message);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let effectWasCancelled = false;

    const renderGoogleButton = async () => {
      if (!GOOGLE_CLIENT_ID) {
        setRenderStatus("error");
        setErrorMessage("Google authentication is currently unavailable.");
        return;
      }

      if (!["signin", "signup"].includes(mode)) {
        setRenderStatus("error");
        setErrorMessage("Invalid Google authentication mode.");
        return;
      }

      if (!googleButtonRef.current) {
        return;
      }

      setRenderStatus("loading");
      setErrorMessage("");

      try {
        const google = await loadGoogleIdentityServices();

        if (effectWasCancelled || !googleButtonRef.current) {
          return;
        }

        const buttonContainer = googleButtonRef.current;

        buttonContainer.replaceChildren();

        const measuredWidth = Math.floor(
          buttonContainer.getBoundingClientRect().width,
        );

        const buttonWidth = Math.min(Math.max(measuredWidth || 320, 200), 400);

        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });

        google.accounts.id.renderButton(buttonContainer, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: mode === "signup" ? "signup_with" : "signin_with",
          shape: "rectangular",
          logo_alignment: "left",
          width: buttonWidth,
        });

        window.requestAnimationFrame(() => {
          if (effectWasCancelled || !googleButtonRef.current) {
            return;
          }

          if (googleButtonRef.current.childElementCount === 0) {
            setRenderStatus("error");
            setErrorMessage("Google authentication is currently unavailable.");
            return;
          }

          setRenderStatus("ready");
        });
      } catch (error) {
        if (effectWasCancelled) {
          return;
        }

        setRenderStatus("error");
        setErrorMessage(
          error?.message || "Google authentication could not be initialized.",
        );
      }
    };

    renderGoogleButton();

    return () => {
      effectWasCancelled = true;

      if (googleButtonRef.current) {
        googleButtonRef.current.replaceChildren();
      }
    };
  }, [handleGoogleResponse, mode]);

  return (
    <div
      className="google-sign-in-component"
      data-google-auth-status={renderStatus}
    >
      <div ref={googleButtonRef} className="google-sign-in-button-container" />

      {renderStatus === "error" && errorMessage && (
        <p className="google-sign-in-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
